import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { Pill } from '../../Pill.js';
import {
  FailedCard,
  fileStem,
  GeneratedDrawer,
  GeneratedGallery,
  plural,
  ResultCard,
} from '../GeneratedGallery.js';
import {
  type GeneratedVideo,
  type GeneratedVideoResult,
  playableUrl,
  strategyLabel,
} from './videoResult.js';

const FALLBACK =
  'The video preview is unavailable. Reopen this outcome to refresh its playback link.';

type DrawerTab = 'overview' | 'elements' | 'tags' | 'ideation';

interface GalleryProps {
  result: GeneratedVideoResult;
}

interface DrawerProps {
  videos: GeneratedVideo[];
  elements: GeneratedVideoResult['storyboard']['elements'];
  active: number | null;
  onActive: (index: number | null) => void;
}

function durationLabel(seconds: number | null): string | null {
  if (seconds === null) return null;
  const rounded = Math.max(0, Math.round(seconds));
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, '0')}`;
}

function hookLine(video: GeneratedVideo): string {
  const scene = video.version?.scenes[0];
  return (
    scene?.on_screen_text ||
    scene?.voiceover ||
    scene?.visual ||
    video.version?.creative_premise ||
    ''
  );
}

function availableTabs(video: GeneratedVideo): { id: DrawerTab; label: string }[] {
  const version = video.version;
  if (version === undefined) return [{ id: 'overview', label: 'Overview' }];
  const elementIds = new Set([
    ...version.creative_direction.element_ids,
    ...version.scenes.flatMap((scene) => scene.element_ids),
  ]);
  return [
    { id: 'overview', label: 'Overview' },
    ...(elementIds.size > 0 ? [{ id: 'elements' as const, label: 'Elements' }] : []),
    ...(version.display_chips.length > 0 ? [{ id: 'tags' as const, label: 'Tags' }] : []),
    ...(version.rationale || version.creative_premise || version.departure_from_references
      ? [{ id: 'ideation' as const, label: 'Ideation' }]
      : []),
  ];
}

function DurationBadge({ seconds }: { seconds: number | null }): ReactNode {
  const label = durationLabel(seconds);
  if (label === null) return null;
  return (
    <Typography
      sx={{
        position: 'absolute',
        top: 10,
        left: 10,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: 'rgba(9,18,31,.72)',
        color: 'common.white',
        fontSize: 11,
      }}
    >
      {label}
    </Typography>
  );
}

function Media({
  video,
  portrait = false,
}: {
  video: GeneratedVideo;
  portrait?: boolean;
}): ReactNode {
  const url = playableUrl(video.playback_url ?? video.video_url);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (url === null || failedUrl === url) return <Alert severity="error">{FALLBACK}</Alert>;
  return (
    <Box
      component="video"
      controls
      playsInline
      preload="metadata"
      src={url}
      aria-label={`Preview ${video.version_name}`}
      onError={() => setFailedUrl(url)}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: portrait ? 420 : 0,
        objectFit: 'cover',
        borderRadius: portrait ? 3 : 0,
        bgcolor: 'common.black',
      }}
    />
  );
}

function TabContent({
  video,
  tab,
  elements,
}: {
  video: GeneratedVideo;
  tab: DrawerTab;
  elements: GeneratedVideoResult['storyboard']['elements'];
}): ReactNode {
  const version = video.version;
  if (version === undefined) return null;
  if (tab === 'elements') {
    const ids = [
      ...new Set([
        ...version.creative_direction.element_ids,
        ...version.scenes.flatMap((scene) => scene.element_ids),
      ]),
    ];
    return (
      <Stack sx={{ gap: 1 }}>
        {ids.map((id) => (
          <Pill key={id}>{elements[id]?.tag ?? id}</Pill>
        ))}
      </Stack>
    );
  }
  if (tab === 'tags') {
    return (
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
        {version.display_chips.map((tag) => (
          <Pill key={tag}>{tag}</Pill>
        ))}
      </Stack>
    );
  }
  if (tab === 'ideation') {
    return (
      <Stack sx={{ gap: 2 }}>
        <Typography sx={{ fontSize: 14 }}>{version.creative_premise}</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{version.rationale}</Typography>
        {version.departure_from_references ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {version.departure_from_references}
          </Typography>
        ) : null}
      </Stack>
    );
  }
  return (
    <Stack sx={{ gap: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Built from your storyboard</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
        {version.creative_premise}
      </Typography>
      <Stack direction="row" sx={{ gap: 5, flexWrap: 'wrap' }}>
        <Stack>
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>ELEMENTS USED</Typography>
          <Typography>{version.creative_direction.element_ids.length}</Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>TONE</Typography>
          <Typography>{version.creative_direction.tone || '—'}</Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>CTA</Typography>
          <Typography>{version.cta || '—'}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

function GeneratedVideoDrawer({ videos, elements, active, onActive }: DrawerProps): ReactNode {
  const [tab, setTab] = useState<DrawerTab>('overview');
  return (
    <GeneratedDrawer
      items={videos}
      active={active}
      noun="creative"
      testId="generated-video-drawer"
      onClose={() => onActive(null)}
      onMove={(next) => {
        setTab('overview');
        onActive(next);
      }}
      render={(video) => ({
        title: video.version_name,
        subtitle: 'Video · Just created',
        tabs: (
          <Tabs value={tab} onChange={(_event, value: DrawerTab) => setTab(value)} sx={{ mt: 2 }}>
            {availableTabs(video).map((entry) => (
              <Tab key={entry.id} value={entry.id} label={entry.label} />
            ))}
          </Tabs>
        ),
        body: (
          <>
            <TabContent video={video} tab={tab} elements={elements} />
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{ gap: 3, borderTop: '1px solid', borderColor: 'divider', pt: 3 }}
            >
              <Box
                sx={{
                  width: { xs: '100%', md: 368 },
                  aspectRatio: '9 / 16',
                  flexShrink: 0,
                  position: 'relative',
                }}
              >
                <Media video={video} portrait />
                <DurationBadge seconds={video.duration_seconds} />
              </Box>
              <Stack sx={{ gap: 3, flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Creative details</Typography>
                <Stack direction="row" sx={{ gap: 5, flexWrap: 'wrap' }}>
                  <Stack>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>DURATION</Typography>
                    <Typography>
                      {video.duration_seconds === null
                        ? '—'
                        : `${Math.round(video.duration_seconds)} sec`}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>STRATEGY</Typography>
                    <Typography>{strategyLabel(video)}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </>
        ),
        download: {
          href: playableUrl(video.playback_url ?? video.video_url),
          fileName: `${fileStem(video.version_name, 'creative')}.mp4`,
          label: 'Download creative',
        },
      })}
    />
  );
}

export function GeneratedVideoGallery({ result }: GalleryProps): ReactNode {
  const ready = result.videos.filter((video) => video.status === 'done');
  const failed = result.videos.filter((video) => video.status === 'failed');
  const [active, setActive] = useState<number | null>(null);

  return (
    <GeneratedGallery
      noun="creative"
      ready={ready.length}
      failed={failed.length}
      readyLine={`${plural(ready.length, 'creative')} ready · ${plural(ready.length, 'outcome')} saved`}
      summary={result.summary}
      gridTestId="generated-video-grid"
      onView={() => setActive(0)}
      drawer={
        <GeneratedVideoDrawer
          videos={ready}
          elements={result.storyboard.elements}
          active={active}
          onActive={setActive}
        />
      }
    >
      {ready.map((video, index) => (
        <ResultCard
          key={video.version_name}
          strategy={video.strategy}
          strategyLabel={strategyLabel(video)}
          title={`Creative ${index + 1} — ${video.version_name}`}
          subtitle={video.version?.display_chips.join(' · ') || 'Video · Just created'}
          mediaSx={{ aspectRatio: '9 / 11', bgcolor: 'grey.900' }}
          onOpen={() => setActive(index)}
        >
          <Media video={video} />
          <DurationBadge seconds={video.duration_seconds} />
          {hookLine(video) ? (
            <Typography
              sx={{
                position: 'absolute',
                left: 16,
                right: 16,
                bottom: 14,
                color: 'common.white',
                fontSize: 13,
                fontWeight: 600,
                textShadow: '0 1px 4px rgba(0,0,0,.9)',
                // Over the native controls: let clicks reach the video.
                pointerEvents: 'none',
              }}
            >
              {hookLine(video)}
            </Typography>
          ) : null}
        </ResultCard>
      ))}
      {failed.map((video) => (
        <FailedCard key={video.version_name} name={video.version_name} error={video.error} />
      ))}
    </GeneratedGallery>
  );
}
